// CanvasView.jsx — Horizontal stepper workflow editor.
import React, { useState, useMemo, useRef, useEffect } from "react";
import canvas from "./CanvasView.module.css";
import shared from "./shared.module.css";
import {
  BiSolidEdit,
  BiSolidToggleLeft,
  BiSolidToggleRight,
  BiSolidSave,
  BiSolidXCircle,
  BiTrash,
} from "react-icons/bi";
import {
  FaArrowRight,
  FaCircle,
  FaAngleDown,
  FaHandPaper,
  FaChevronLeft,
  FaChevronRight,
  FaBolt,
  FaExclamationTriangle,
} from "react-icons/fa";
import StepMappingEditor from "./StepMappingEditor";

import { normaliseTriggerList } from "./triggerAttachment";

const C = { ...canvas, ...shared };

const SCOPE = {
  APP: "app",
  TENANT: "tenant",
};

const defaultChainNode = (api_config_id = "") => ({
  api_config_id,
  manual: false,
  input_mapping: {},
  path_param_mappings: {},
  query_param_mappings: {},
  payload_param_mappings: {},
  post_triggers: [],
  branches: {},
});

function updateNodeAtPath(root, nodePath, updater) {
  if (nodePath.length === 0) return updater(root);
  const [head, ...rest] = nodePath;
  const current = root.branches?.[head] || defaultChainNode("");
  return {
    ...root,
    branches: {
      ...(root.branches || {}),
      [head]: updateNodeAtPath(current, rest, updater),
    },
  };
}

function getNodeAtPath(root, nodePath) {
  let node = root;
  for (const key of nodePath) {
    node = node?.branches?.[key];
    if (!node) return null;
  }
  return node;
}

function removeBranchAtPath(root, nodePath, statusCode) {
  return updateNodeAtPath(root, nodePath, (node) => {
    const nextBranches = { ...(node.branches || {}) };
    delete nextBranches[statusCode];
    return { ...node, branches: nextBranches };
  });
}

function sortBranchKeys(keys) {
  return [...keys].sort((a, b) => {
    const aNum = a === "default" ? Number.POSITIVE_INFINITY : parseInt(a, 10);
    const bNum = b === "default" ? Number.POSITIVE_INFINITY : parseInt(b, 10);
    if (Number.isNaN(aNum) || Number.isNaN(bNum)) return a.localeCompare(b);
    return aNum - bNum;
  });
}

function pruneEmptyBranches(node) {
  if (!node || typeof node !== "object") return node;
  const prunedBranches = {};
  for (const [status, child] of Object.entries(node.branches || {})) {
    const prunedChild = pruneEmptyBranches(child);
    const hasApi = !!prunedChild.api_config_id;
    const hasChildren = Object.keys(prunedChild.branches || {}).length > 0;
    if (hasApi || hasChildren) {
      prunedBranches[status] = prunedChild;
    }
  }
  return { ...node, branches: prunedBranches };
}

function normalizeChainNode(node) {
  if (!node || typeof node !== "object") return null;
  return {
    api_config_id: node.api_config_id || "",
    manual: !!node.manual,
    input_mapping: node.input_mapping || {},
    path_param_mappings: node.path_param_mappings || {},
    query_param_mappings: node.query_param_mappings || {},
    payload_param_mappings: node.payload_param_mappings || {},
    post_triggers: normaliseTriggerList(node.post_triggers),
    branches: Object.fromEntries(
      Object.entries(node.branches || {}).map(([status, child]) => [
        status,
        normalizeChainNode(child) || defaultChainNode(""),
      ]),
    ),
  };
}

function statusClass(status) {
  if (status === "default") return "sDefault";
  const n = parseInt(status, 10);
  if (Number.isNaN(n)) return "sDefault";
  if (n >= 200 && n < 300) return "s2xx";
  if (n >= 300 && n < 400) return "s3xx";
  if (n >= 400 && n < 500) return "s4xx";
  if (n >= 500) return "s5xx";
  return "sDefault";
}

function displayUrlOf(fullUrl) {
  if (!fullUrl) return "";
  const m = String(fullUrl).match(/^[a-z][a-z0-9+.-]*:\/\/[^/]+(\/.*)?$/i);
  if (m) return m[1] || "/";
  return fullUrl;
}

function analyzeChain(root) {
  const perNodeStage = {};
  let totalNodes = 0;
  let nodesWithApi = 0;
  let totalEdges = 0;
  let edgesMapped = 0;

  const visit = (node, nodePath) => {
    if (!node) return;
    totalNodes += 1;
    const key = nodePath.join("/") || "root";
    const hasApi = !!node.api_config_id;
    if (hasApi) nodesWithApi += 1;

    const childKeys = Object.keys(node.branches || {});
    let nodeStage;

    if (!hasApi) {
      nodeStage = "empty";
    } else {
      let anyChildMissingApi = false;
      let anyChildMissingMapping = false;

      for (const status of childKeys) {
        const child = node.branches[status];
        const childHasApi = !!child?.api_config_id;
        if (!childHasApi) {
          anyChildMissingApi = true;
          continue;
        }
        totalEdges += 1;
        const mappingEntries =
          Object.keys(child.path_param_mappings || {}).length +
          Object.keys(child.query_param_mappings || {}).length +
          Object.keys(child.payload_param_mappings || {}).length +
          Object.keys(child.input_mapping || {}).length;
        if (mappingEntries > 0) {
          edgesMapped += 1;
        } else {
          anyChildMissingMapping = true;
        }
      }

      if (childKeys.length === 0) {
        nodeStage = "done";
      } else if (anyChildMissingApi || anyChildMissingMapping) {
        nodeStage = "needsMap";
      } else {
        nodeStage = "done";
      }
    }

    perNodeStage[key] = nodeStage;

    for (const status of childKeys) {
      visit(node.branches[status], [...nodePath, status]);
    }
  };

  visit(root, []);
  return { perNodeStage, totalNodes, nodesWithApi, totalEdges, edgesMapped };
}

function isIncomplete(root, sel) {
  if (sel.kind === "node") {
    const node = getNodeAtPath(root, sel.nodePath);
    if (!node) return true;
    if (!node.api_config_id) return true;
    return false;
  }
  const parent = getNodeAtPath(root, sel.nodePath);
  const child = parent?.branches?.[sel.status];
  if (!child?.api_config_id) return true;
  const entries =
    Object.keys(child.path_param_mappings || {}).length +
    Object.keys(child.query_param_mappings || {}).length +
    Object.keys(child.payload_param_mappings || {}).length +
    Object.keys(child.input_mapping || {}).length;
  return entries === 0;
}

function findNextIncomplete(root, from) {
  const flatten = [];
  const collect = (node, nodePath) => {
    flatten.push({ kind: "node", nodePath });
    const childKeys = sortBranchKeys(Object.keys(node.branches || {}));
    for (const status of childKeys) {
      const child = node.branches[status];
      if (!child?.api_config_id) {
        flatten.push({ kind: "node", nodePath: [...nodePath, status] });
        continue;
      }
      const mappingEntries =
        Object.keys(child.path_param_mappings || {}).length +
        Object.keys(child.query_param_mappings || {}).length +
        Object.keys(child.payload_param_mappings || {}).length +
        Object.keys(child.input_mapping || {}).length;
      if (mappingEntries === 0) {
        flatten.push({ kind: "edge", nodePath, status });
      }
      collect(child, [...nodePath, status]);
    }
  };
  collect(root, []);

  const fromKey =
    from.kind === "edge"
      ? `edge:${from.nodePath.join("/")}:${from.status}`
      : `node:${from.nodePath.join("/")}`;
  const fromIdx = flatten.findIndex((s) =>
    s.kind === "edge"
      ? `edge:${s.nodePath.join("/")}:${s.status}` === fromKey
      : `node:${s.nodePath.join("/")}` === fromKey,
  );
  const start = fromIdx >= 0 ? fromIdx + 1 : 0;

  for (let i = start; i < flatten.length; i++) {
    if (isIncomplete(root, flatten[i])) return flatten[i];
  }
  for (let i = 0; i < start; i++) {
    if (isIncomplete(root, flatten[i])) return flatten[i];
  }
  return null;
}

const STAGE_GLYPHS = { empty: "!", needsMap: "✎", done: "✓", manual: "✋" };
const STAGE_TITLES = {
  empty: "No API selected",
  needsMap: "Needs configuration",
  done: "Done",
  manual: "Manual (waits for webhook)",
};

const CanvasView = ({
  chains,
  apiConfigs,
  onEdit,
  onDelete,
  onToggle,
  onSave,
  tenantId,
  appName,
  operations = [],
  connectedProviders = [],
  // ⭐ P2: 403 inline banner on save. The parent passes an
  // `attachmentRefusal` when a save trips the assert_attached guard,
  // and the canvas shows it at the top of the editing chain so the
  // operator can navigate away to attach the resource and retry.
  attachmentRefusal = null,
  onClearAttachmentRefusal = () => {},
}) => {
  const [editingChainId, setEditingChainId] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [currentStepIdx, setCurrentStepIdx] = useState(0);
  const [expandedEdgeKey, setExpandedEdgeKey] = useState(null);

  const stripRef = useRef(null);

  const getApiConfig = (apiConfigId) =>
    apiConfigs.find((c) => c.api_config_id === apiConfigId);
  const getApiUrl = (apiConfigId) =>
    getApiConfig(apiConfigId)?.api_config?.url || "";
  const getApiMethod = (apiConfigId) =>
    getApiConfig(apiConfigId)?.api_config?.method || "GET";

  const getPlaceholderFields = (apiConfigId) => {
    const cfg = getApiConfig(apiConfigId);
    if (!cfg) return [];
    const payloadTemplate = cfg.api_config?.payload_template || "{}";
    const matches = payloadTemplate.match(/\{\{\s*(\w+)\s*\}\}/g) || [];
    return matches.map((m) => m.replace(/[\{\}\s]/g, ""));
  };

  const getSampleResponses = (apiConfigId) => {
    const cfg = getApiConfig(apiConfigId);
    return cfg?.api_config?.sample_responses_by_status || {};
  };

  const startEditing = (chain) => {
    setEditingChainId(chain.chain_id);
    setEditForm({
      chain_id: chain.chain_id,
      scope: chain.scope || SCOPE.APP,
      name: chain.name || "",
      enabled: chain.enabled !== false,
      root: normalizeChainNode(chain.root) || defaultChainNode(""),
    });
    setCurrentStepIdx(0);
    setExpandedEdgeKey(null);
    // Clearing the parent's refusal state when the operator opens a
    // different chain keeps the banner from leaking between editing
    // sessions.
    onClearAttachmentRefusal();
  };

  const cancelEditing = () => {
    setEditingChainId(null);
    setEditForm(null);
    setCurrentStepIdx(0);
    setExpandedEdgeKey(null);
  };

  const handleNodeApiChange = (nodePath, newApiConfigId) => {
    setEditForm((prev) => ({
      ...prev,
      root: updateNodeAtPath(prev.root, nodePath, (node) => {
        if (newApiConfigId === node.api_config_id) return node;
        const newCfg = getApiConfig(newApiConfigId);
        const declaredStatuses = Object.keys(
          newCfg?.api_config?.sample_responses_by_status || {},
        );
        const seeded = {};
        for (const status of declaredStatuses) {
          seeded[status] = node.branches?.[status] || defaultChainNode("");
        }
        return { ...node, api_config_id: newApiConfigId, branches: seeded };
      }),
    }));
  };

  const handleNodeManualToggle = (nodePath, checked) => {
    setEditForm((prev) => ({
      ...prev,
      root: updateNodeAtPath(prev.root, nodePath, (node) => ({
        ...node,
        manual: !!checked,
      })),
    }));
  };

  const handleDeleteBranch = (nodePath, statusCode) => {
    if (
      !window.confirm(
        `Delete branch "${statusCode}"? Any sub-branches below it will also be removed.`,
      )
    ) {
      return;
    }
    setEditForm((prev) => ({
      ...prev,
      root: removeBranchAtPath(prev.root, nodePath, statusCode),
    }));
    if (expandedEdgeKey === `${nodePath.join("/")}|${statusCode}`) {
      setExpandedEdgeKey(null);
    }
  };

  const handleBranchMappingChange = (
    nodePath,
    statusCode,
    key,
    newKey,
    newValue,
    fullMapping,
  ) => {
    setEditForm((prev) => {
      const fullPath = [...nodePath, statusCode];
      return {
        ...prev,
        root: updateNodeAtPath(prev.root, fullPath, (node) => {
          let newMapping;
          if (fullMapping) {
            newMapping = fullMapping;
          } else {
            newMapping = { ...(node.input_mapping || {}) };
            if (key && key !== newKey) delete newMapping[key];
            newMapping[newKey] = newValue;
          }
          return { ...node, input_mapping: newMapping };
        }),
      };
    });
  };

  const handleBranchParamMappingsChange = (
    nodePath,
    statusCode,
    { path, query, payload },
  ) => {
    setEditForm((prev) => {
      const fullPath = [...nodePath, statusCode];
      return {
        ...prev,
        root: updateNodeAtPath(prev.root, fullPath, (node) => ({
          ...node,
          path_param_mappings: path || {},
          query_param_mappings: query || {},
          payload_param_mappings: payload || {},
        })),
      };
    });
  };

  const handleSave = async () => {
    if (!editForm) return;
    if (!tenantId) {
      alert("Cannot save chain: tenant_id is missing. Please reload the page.");
      return;
    }
    if (!appName) {
      alert("Cannot save chain: app_name is missing. Please reload the page.");
      return;
    }
    const payload = {
      tenant_id: tenantId,
      app_name: appName,
      scope: editForm.scope || SCOPE.APP,
      name: editForm.name,
      root: pruneEmptyBranches(editForm.root),
      enabled: editForm.enabled,
    };
    if (editForm.chain_id) payload.chain_id = editForm.chain_id;
    try {
      if (onSave) await onSave(payload);
      cancelEditing();
    } catch (e) {
      alert("Error saving chain");
    }
  };

  const analysis = useMemo(
    () => (editForm ? analyzeChain(editForm.root) : null),
    [editForm],
  );

  const pathKey = (p) => p.join("/") || "root";

  const chainSteps = useMemo(() => {
    if (!editForm) return [];
    const steps = [];
    const visit = (node, nodePath) => {
      steps.push({
        nodePath,
        parentPath: nodePath.length > 0 ? nodePath.slice(0, -1) : null,
        status: nodePath.length > 0 ? nodePath[nodePath.length - 1] : null,
        node,
      });
      const childKeys = sortBranchKeys(Object.keys(node.branches || {}));
      for (const status of childKeys) {
        const child = node.branches[status];
        if (!child) continue;
        visit(child, [...nodePath, status]);
      }
    };
    visit(editForm.root, []);
    return steps;
  }, [editForm]);

  useEffect(() => {
    if (currentStepIdx >= chainSteps.length) {
      setCurrentStepIdx(Math.max(0, chainSteps.length - 1));
    }
  }, [chainSteps.length, currentStepIdx]);

  useEffect(() => {
    if (!stripRef.current) return;
    const el = stripRef.current.querySelector(
      `[data-step-idx="${currentStepIdx}"]`,
    );
    if (el) {
      el.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    }
  }, [currentStepIdx]);

  const currentStep = chainSteps[currentStepIdx] || null;
  const goPrev = () => setCurrentStepIdx((i) => Math.max(0, i - 1));
  const goNext = () =>
    setCurrentStepIdx((i) => Math.min(chainSteps.length - 1, i + 1));

  const goNextIncomplete = () => {
    if (!editForm) return;
    const next = findNextIncomplete(editForm.root, {
      kind: "node",
      nodePath: currentStep?.nodePath || [],
    });
    if (!next) return;
    if (next.kind === "node") {
      const idx = chainSteps.findIndex(
        (s) => s.nodePath.join("/") === next.nodePath.join("/"),
      );
      if (idx >= 0) setCurrentStepIdx(idx);
      setExpandedEdgeKey(null);
    } else {
      const childPath = [...next.nodePath, next.status];
      const idx = chainSteps.findIndex(
        (s) => s.nodePath.join("/") === childPath.join("/"),
      );
      if (idx >= 0) setCurrentStepIdx(idx);
      setExpandedEdgeKey(`${next.nodePath.join("/")}|${next.status}`);
    }
  };

  const renderStageIcon = (stage, size = 16) => (
    <span
      className={`${C.treeRowStageIcon} ${C[stage]}`}
      style={{ width: size, height: size, fontSize: size - 5 }}
      title={STAGE_TITLES[stage]}
      aria-label={STAGE_TITLES[stage]}
    >
      {STAGE_GLYPHS[stage] || "•"}
    </span>
  );

  const renderProgressStrip = (a) => {
    if (!a) return null;
    const { totalNodes, nodesWithApi, totalEdges, edgesMapped } = a;
    const structureDone = nodesWithApi === totalNodes;
    const mappingDone = totalEdges === 0 || edgesMapped === totalEdges;
    const structureClass = structureDone
      ? "done"
      : nodesWithApi === 0
        ? "pending"
        : "partial";
    const mappingClass = mappingDone
      ? "done"
      : edgesMapped === 0
        ? "pending"
        : "partial";
    return (
      <div className={C.progressStrip}>
        <div className={C.progressPass}>
          <span className={C.progressPassLabel}>1. Pick APIs</span>
          <span className={`${C.progressPassCount} ${C[structureClass]}`}>
            {nodesWithApi}/{totalNodes}
          </span>
        </div>
        <span className={C.progressDivider}>→</span>
        <div className={C.progressPass}>
          <span className={C.progressPassLabel}>2. Configure mappings</span>
          <span className={`${C.progressPassCount} ${C[mappingClass]}`}>
            {edgesMapped}/{totalEdges}
          </span>
        </div>
        <span className={C.progressHint}>
          {structureDone && mappingDone
            ? "✓ Chain complete"
            : structureDone
              ? "Structure ready — now configure each branch mapping"
              : "Pick an API for every node first"}
        </span>
      </div>
    );
  };

  const renderStepRail = () => {
    if (!editForm || chainSteps.length === 0) return null;
    return (
      <div className={C.stepRail}>
        {chainSteps.map((step, i) => {
          const stage =
            analysis?.perNodeStage?.[pathKey(step.nodePath)] || "empty";
          const isCurrent = i === currentStepIdx;
          return (
            <React.Fragment key={pathKey(step.nodePath)}>
              {i > 0 && <span className={C.stepRailSep}>›</span>}
              <button
                className={`${C.stepRailPill} ${
                  isCurrent ? C.stepRailPillActive : ""
                }`}
                onClick={() => setCurrentStepIdx(i)}
                title={`Step ${i + 1}: ${getApiUrl(step.node.api_config_id) || "unset"}`}
              >
                <span className={C.stepRailNum}>{i + 1}</span>
                {renderStageIcon(stage, 12)}
                {step.status && (
                  <span
                    className={`${C.treeRowStatusPill} ${C[statusClass(step.status)]}`}
                    style={{ fontSize: 9, padding: "1px 5px" }}
                  >
                    {step.status}
                  </span>
                )}
              </button>
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  const renderNodeCard = (step, stepIdx) => {
    const { node, nodePath, status, parentPath } = step;
    const isRoot = nodePath.length === 0;
    const apiConfigId = node.api_config_id;
    const fullUrl = getApiUrl(apiConfigId);
    const truncatedUrl = displayUrlOf(fullUrl);
    const method = apiConfigId ? getApiMethod(apiConfigId) : null;
    const isManual = !!node.manual;
    const isFocused = stepIdx === currentStepIdx;
    const stage = analysis?.perNodeStage?.[pathKey(nodePath)] || "empty";
    const nodeFields = getPlaceholderFields(apiConfigId);

    const postTriggerCount = Array.isArray(node.post_triggers)
      ? node.post_triggers.length
      : 0;

    return (
      <div
        className={`${C.canvasCard2} ${
          isFocused ? C.canvasCard2Focused : ""
        } ${isManual ? C.canvasCard2Manual : ""} ${
          !apiConfigId ? C.canvasCard2Empty : ""
        }`}
        data-step-idx={stepIdx}
        onClick={() => setCurrentStepIdx(stepIdx)}
      >
        <div className={C.canvasCard2Header}>
          <div className={C.canvasCard2Title}>
            {renderStageIcon(stage, 16)}
            <span style={{ marginLeft: 6 }}>
              {isRoot ? "Root API" : "API"}
            </span>
            {status && (
              <span
                className={`${C.treeRowStatusPill} ${C[statusClass(status)]}`}
                style={{ marginLeft: 8 }}
              >
                {status}
              </span>
            )}
            {isManual && (
              <span className={C.manualBadge}>
                <FaHandPaper size={11} /> Manual
              </span>
            )}
            {postTriggerCount > 0 && (
              <span
                className={C.postTriggerBadge}
                title={
                  `${postTriggerCount} parallel action${
                    postTriggerCount === 1 ? "" : "s"
                  } attached — fires in the background when this API returns`
                }
              >
                <FaBolt size={9} /> {postTriggerCount}
              </span>
            )}
          </div>
          {!isRoot && (
            <button
              className={C.deleteBranchBtn}
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteBranch(parentPath, status);
              }}
              title="Delete this branch"
            >
              <BiTrash size={14} />
            </button>
          )}
        </div>

        <div className={C.selectWrapper} style={{ marginTop: 8 }}>
          <select
            value={apiConfigId}
            onChange={(e) => {
              e.stopPropagation();
              handleNodeApiChange(nodePath, e.target.value);
            }}
            className={C.canvasSelect}
            onClick={(e) => e.stopPropagation()}
          >
            <option value="">Select API</option>
            {apiConfigs.filter((c) => c.scope === "app" || !c.scope).length >
              0 && (
              <optgroup label="App Level (this app)">
                {apiConfigs
                  .filter((c) => c.scope === "app" || !c.scope)
                  .map((cfg) => (
                    <option
                      key={cfg.api_config_id}
                      value={cfg.api_config_id}
                    >
                      {cfg.api_config?.url || cfg.api_config_id}
                    </option>
                  ))}
              </optgroup>
            )}
            {apiConfigs.filter((c) => c.scope === "tenant").length > 0 && (
              <optgroup label="Shared (tenant-wide)">
                {apiConfigs
                  .filter((c) => c.scope === "tenant")
                  .map((cfg) => (
                    <option
                      key={cfg.api_config_id}
                      value={cfg.api_config_id}
                    >
                      {cfg.api_config?.url || cfg.api_config_id}
                    </option>
                  ))}
              </optgroup>
            )}
          </select>
          <FaAngleDown className={C.selectIcon} />
        </div>

        {apiConfigId && (
          <div
            className={C.canvasNodeUrl}
            style={{ marginTop: 8, cursor: "help" }}
            title={fullUrl}
          >
            <span className={C.canvasNodeMethod}>{method}</span>
            {truncatedUrl}
          </div>
        )}

        {apiConfigId && nodeFields.length > 0 && (
          <div className={C.canvasNodeMeta}>
            <span>Uses data from:</span>
            <ul className={C.canvasNodeDataList}>
              {nodeFields.map((field) => (
                <li key={field}>• {field}</li>
              ))}
            </ul>
          </div>
        )}

        <label
          className={C.canvasManualToggle}
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="checkbox"
            checked={isManual}
            onChange={(e) =>
              handleNodeManualToggle(nodePath, e.target.checked)
            }
          />
          <span>Manual — pause for external webhook</span>
        </label>

        {isManual && (
          <div className={C.canvasManualHint}>
            Sageion will <strong>not call this API</strong>. The chain pauses
            here and waits for <code>/webhook/callback</code> with a{" "}
            <code>status_code</code> and completion data.
          </div>
        )}
      </div>
    );
  };

  const renderEdgeArrow = (parentStep, childStep, stepIdx) => {
    const child = childStep.node;
    const status = childStep.status;
    const parentPath = parentStep.nodePath;
    const edgeKey = `${parentPath.join("/")}|${status}`;
    const isActive = expandedEdgeKey === edgeKey;

    const mappingCount =
      Object.keys(child.path_param_mappings || {}).length +
      Object.keys(child.query_param_mappings || {}).length +
      Object.keys(child.payload_param_mappings || {}).length;
    const isMapped = mappingCount > 0;
    const childHasApi = !!child.api_config_id;

    const handleClick = (e) => {
      e.stopPropagation();
      const next = isActive ? null : edgeKey;
      setExpandedEdgeKey(next);

      if (next) {
        const childIdx = chainSteps.findIndex(
          (s) => s.nodePath.join("/") === childStep.nodePath.join("/"),
        );
        if (childIdx >= 0) setCurrentStepIdx(childIdx);

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const el = document.querySelector(
              `[data-mapping-panel="${edgeKey}"]`,
            );
            if (el) {
              el.scrollIntoView({ behavior: "smooth", block: "start" });
            }
          });
        });
      }
    };

    return (
      <button
        key={`edge-${stepIdx}`}
        type="button"
        className={`${C.edgeArrowBtn} ${
          isActive ? C.edgeArrowBtnActive : ""
        } ${isMapped ? C.edgeArrowBtnDone : C.edgeArrowBtnPending}`}
        onClick={handleClick}
        title={
          childHasApi
            ? isMapped
              ? `Edit mapping · ${mappingCount} params mapped — click to open`
              : "Click to configure the mapping"
            : "Pick the child API on the right first"
        }
      >
        <span
          className={`${C.edgeArrowBtnStatus} ${C[statusClass(status)]}`}
        >
          {status}
        </span>
        <span className={C.edgeArrowBtnArrow}>→</span>
        <span className={C.edgeArrowBtnLabel}>
          {isMapped
            ? `Edit ${mappingCount}`
            : childHasApi
              ? "Map"
              : "Set up"}
        </span>
        <span className={C.edgeArrowBtnIcon} aria-hidden="true">
          ✎
        </span>
      </button>
    );
  };

  const renderFilmstrip = () => {
    if (chainSteps.length === 0) return null;

    const slices = [];
    for (let i = 0; i < chainSteps.length; i++) {
      const step = chainSteps[i];
      if (i > 0) {
        const parentStep = chainSteps.find(
          (s) =>
            s.nodePath.join("/") === (step.parentPath || []).join("/"),
        );
        if (parentStep) {
          slices.push(
            <React.Fragment key={`arrow-${i}`}>
              {renderEdgeArrow(parentStep, step, i)}
            </React.Fragment>,
          );
        }
      }
      slices.push(
        <div key={`card-${i}`} className={C.canvasStep} data-step-idx={i}>
          {renderNodeCard(step, i)}
        </div>,
      );
    }

    const activeEdge = (() => {
      if (!expandedEdgeKey || !editForm) return null;
      const [parentKey, status] = expandedEdgeKey.split("|");
      const parentPath =
        parentKey === "" || parentKey === "root" ? [] : parentKey.split("/");
      const parent = getNodeAtPath(editForm.root, parentPath);
      if (!parent) return null;
      const child = parent.branches?.[status];
      if (!child) return null;
      return { parentPath, parent, child, status };
    })();

    const activeMappingCount = activeEdge
      ? Object.keys(activeEdge.child.path_param_mappings || {}).length +
        Object.keys(activeEdge.child.query_param_mappings || {}).length +
        Object.keys(activeEdge.child.payload_param_mappings || {}).length
      : 0;

    return (
      <>
        <div className={C.filmstripOuter}>
          <button
            className={C.stripNavBtn}
            onClick={goPrev}
            disabled={currentStepIdx === 0}
            title="Previous step"
          >
            <FaChevronLeft size={14} />
          </button>

          <div className={C.filmstrip} ref={stripRef}>
            <div className={C.canvasStartNode}>
              <FaCircle className={C.startIcon} />
              <span className={C.canvasNodeLabel}>
                {editForm.name || "Unnamed Flow"} starts
              </span>
              <div className={C.canvasNodeSub}>When flow is triggered</div>
            </div>
            <FaArrowRight className={C.canvasArrow} />
            {slices}
            <FaArrowRight className={C.canvasArrow} />
            <div className={C.canvasStartNode}>
              <span className={C.canvasNodeLabel}>End</span>
              <div className={C.canvasNodeSub}>Flow completed</div>
            </div>
          </div>

          <button
            className={C.stripNavBtn}
            onClick={goNext}
            disabled={currentStepIdx === chainSteps.length - 1}
            title="Next step"
          >
            <FaChevronRight size={14} />
          </button>
        </div>

        {activeEdge && (
          <div
            className={C.mappingPanelHost}
            data-mapping-panel={`${activeEdge.parentPath.join("/")}|${activeEdge.status}`}
          >
            <div className={C.mappingPanelHeader}>
              <span
                className={`${C.treeRowStatusPill} ${C[statusClass(activeEdge.status)]}`}
              >
                {activeEdge.status}
              </span>
              <span className={C.mappingPanelHeaderStrong}>
                Mapping{" "}
                {displayUrlOf(
                  getApiUrl(activeEdge.parent.api_config_id),
                ) || "parent"}{" "}
                →{" "}
                {displayUrlOf(
                  getApiUrl(activeEdge.child.api_config_id),
                ) || "child"}
              </span>
              {activeEdge.child.manual && (
                <span className={C.manualBadge}>
                  <FaHandPaper size={11} /> Manual
                </span>
              )}
              {activeMappingCount > 0 && (
                <span className={C.edgeDoneBadge}>
                  ✓ {activeMappingCount} mapped
                </span>
              )}
              <button
                className={C.mappingPanelClose}
                onClick={() => setExpandedEdgeKey(null)}
              >
                <BiSolidXCircle size={14} /> Close
              </button>
            </div>

            <div className={C.mappingPanelBody}>
              {activeEdge.child.api_config_id ? (
                <StepMappingEditor
                  stepIndex={activeEdge.parentPath.length}
                  previousStepApiConfigId={activeEdge.parent.api_config_id}
                  stepApiConfigId={activeEdge.child.api_config_id}
                  mapping={activeEdge.child.input_mapping || {}}
                  onMappingChange={(
                    _stepIdx,
                    _statusCode,
                    key,
                    newKey,
                    newValue,
                    fullMapping,
                  ) =>
                    handleBranchMappingChange(
                      activeEdge.parentPath,
                      activeEdge.status,
                      key,
                      newKey,
                      newValue,
                      fullMapping,
                    )
                  }
                  pathParamMappings={
                    activeEdge.child.path_param_mappings || {}
                  }
                  queryParamMappings={
                    activeEdge.child.query_param_mappings || {}
                  }
                  payloadParamMappings={
                    activeEdge.child.payload_param_mappings || {}
                  }
                  onParamMappingsChange={(_stepIdx, _statusCode, payload) =>
                    handleBranchParamMappingsChange(
                      activeEdge.parentPath,
                      activeEdge.status,
                      payload,
                    )
                  }
                  apiConfigs={apiConfigs}
                  statusCode={activeEdge.status}
                  sampleResponseBody={
                    getSampleResponses(activeEdge.parent.api_config_id)[
                      activeEdge.status
                    ] ?? null
                  }
                  leadFieldNames={getPlaceholderFields(
                    activeEdge.parent.api_config_id,
                  )}
                />
              ) : (
                <p className={C.firstStepHint}>
                  Pick the child API on the card to the right first, then
                  click this arrow again.
                </p>
              )}

              <div className={C.edgeNextBar}>
                <span className={C.edgeNextLabel}>
                  {activeMappingCount > 0
                    ? "This branch looks good."                    : "Add at least one mapping to mark this branch done."}
                </span>
                <button
                  className={C.edgeNextBtn}
                  onClick={(e) => {
                    e.stopPropagation();
                    goNextIncomplete();
                  }}
                >
                  Next incomplete →
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  const renderFocusHeader = () => {
    if (!currentStep) return null;
    const stage =
      analysis?.perNodeStage?.[pathKey(currentStep.nodePath)] || "empty";
    const url =
      getApiUrl(currentStep.node.api_config_id) || "No API selected";
    const isRoot = currentStep.nodePath.length === 0;
    return (
      <div className={C.focusHeader}>
        <span className={C.focusHeaderStep}>
          Step {currentStepIdx + 1} of {chainSteps.length}
        </span>
        {renderStageIcon(stage, 14)}
        <span className={C.focusHeaderLabel}>
          {isRoot ? "Root" : `Branch ${currentStep.status}`}
        </span>
        <span className={C.focusHeaderUrl} title={url}>
          {displayUrlOf(url) || url}
        </span>
        <button className={C.focusHeaderNext} onClick={goNextIncomplete}>
          Jump to next incomplete →
        </button>
      </div>
    );
  };

  const renderEditableChain = () => (
    <div className={C.canvasChain}>
      <div className={C.canvasHeader}>
        <div className={C.canvasChainInfo}>
          <input
            type="text"
            value={editForm.name}
            onChange={(e) =>
              setEditForm({ ...editForm, name: e.target.value })
            }
            className={C.canvasNameInput}
            placeholder="Enter chain name"
          />
          <label className={C.canvasToggle}>
            <input
              type="checkbox"
              checked={editForm.enabled}
              onChange={(e) =>
                setEditForm({ ...editForm, enabled: e.target.checked })
              }
            />
            <span>{editForm.enabled ? "Live" : "Disabled"}</span>
          </label>
          <span className={C.workflowBadge}>Workflow</span>
        </div>
        <div className={C.canvasActions}>
          <button onClick={handleSave} className={C.saveButton}>
            <BiSolidSave size={18} /> Save
          </button>
          <button onClick={cancelEditing} className={C.cancelButton}>
            <BiSolidXCircle size={18} /> Cancel
          </button>
        </div>
      </div>

      {/* ⭐ P2: inline 403 banner on save. Rendered inside the canvas
          editor when a chain save trips the attachment guard (e.g.
          a chain node references a tenant-scoped api_config the app
          hasn't attached yet). Dismissible; navigating away to
          APIConfig → Tenant Level and attaching the resource, then
          clicking Save again, is the expected recovery path. */}
      {attachmentRefusal && (
        <div
          role="alert"
          style={{
            marginBottom: 12,
            padding: "12px 16px",
            backgroundColor: "#fff8e1",
            border: "1px solid #fde68a",
            borderLeft: "3px solid #d97706",
            borderRadius: 6,
            color: "#78350f",
            fontSize: 13,
            lineHeight: 1.55,
            display: "flex",
            alignItems: "flex-start",
            gap: 10,
          }}
        >
          <FaExclamationTriangle
            size={18}
            style={{ flexShrink: 0, marginTop: 2 }}
          />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>
              Can&apos;t save this chain — a referenced{" "}
              {attachmentRefusal.resourceType === "chain"
                ? "chain"
                : "API config"}{" "}
              isn&apos;t attached to this app.
            </div>
            <div>{attachmentRefusal.message}</div>
            <div style={{ marginTop: 6, color: "#92400e" }}>
              Open{" "}
              <strong>
                {attachmentRefusal.resourceType === "chain"
                  ? "ChainApis → Tenant Level"
                  : "API Config → Tenant Level"}
              </strong>{" "}
              and attach this{" "}
              {attachmentRefusal.resourceType === "chain"
                ? "chain"
                : "config"}{" "}
              to this app, then click Save again. Your edits are preserved.
            </div>
          </div>
          <button
            type="button"
            onClick={onClearAttachmentRefusal}
            style={{
              background: "none",
              border: "none",
              color: "#78350f",
              cursor: "pointer",
              fontSize: 16,
              lineHeight: 1,
              padding: 4,
            }}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {renderProgressStrip(analysis)}
      {renderStepRail()}
      {renderFocusHeader()}
      {renderFilmstrip()}
    </div>
  );

  const renderViewOnlyChain = (chain) => {
    const root = normalizeChainNode(chain.root) || defaultChainNode("");
    const flat = [];
    const visit = (node, nodePath) => {
      flat.push({ node, nodePath });
      const childKeys = sortBranchKeys(Object.keys(node.branches || {}));
      for (const status of childKeys) {
        if (node.branches[status]) {
          visit(node.branches[status], [...nodePath, status]);
        }
      }
    };
    visit(root, []);

    return (
      <div className={C.canvasChain}>
        <div className={C.canvasHeader}>
          <div className={C.canvasChainInfo}>
            <h4 className={C.canvasChainName}>{chain.name}</h4>
            <span
              className={
                chain.enabled !== false ? C.enabledBadge : C.disabledBadge
              }
            >
              {chain.enabled !== false ? "Live" : "Disabled"}
            </span>
            <span className={C.workflowBadge}>Workflow</span>
          </div>
          <div className={C.canvasActions}>
            <button
              onClick={() => onToggle(chain)}
              className={C.iconBtn}
              title="Toggle chain"
            >
              {chain.enabled !== false ? (
                <BiSolidToggleRight size={48} color="green" />
              ) : (
                <BiSolidToggleLeft size={48} color="gray" />
              )}
            </button>
            <button
              onClick={() => startEditing(chain)}
              className={C.editButton}
              title="Edit chain"
            >
              <BiSolidEdit size={18} />
            </button>
            <button
              onClick={() => onDelete(chain.chain_id)}
              className={C.deleteButton}
              title="Delete chain"
            >
              <BiTrash size={18} />
            </button>
          </div>
        </div>

        <div className={C.canvasFlow}>
          <div className={C.canvasStartNode}>
            <FaCircle className={C.startIcon} />
            <span className={C.canvasNodeLabel}>
              {chain.name || "Unnamed Flow"} starts
            </span>
            <div className={C.canvasNodeSub}>When flow is triggered</div>
          </div>
          <FaArrowRight className={C.canvasArrow} />
          {flat.map((step, i) => {
            const postTriggerCount = Array.isArray(step.node.post_triggers)
              ? step.node.post_triggers.length
              : 0;
            return (
              <React.Fragment key={step.nodePath.join("/") || "root"}>
                {i > 0 && <FaArrowRight className={C.canvasArrow} />}
                <div className={C.canvasCard2}>
                  <div className={C.canvasCard2Header}>
                    <div className={C.canvasCard2Title}>
                      {step.nodePath.length > 0 && (
                        <span
                          className={`${C.treeRowStatusPill} ${C[statusClass(step.nodePath[step.nodePath.length - 1])]}`}
                        >
                          {step.nodePath[step.nodePath.length - 1]}
                        </span>
                      )}
                      {step.node.manual && (
                        <span className={C.manualBadge}>
                          <FaHandPaper size={11} /> Manual
                        </span>
                      )}
                      {postTriggerCount > 0 && (
                        <span
                          className={C.postTriggerBadge}
                          title={`${postTriggerCount} parallel action${
                            postTriggerCount === 1 ? "" : "s"
                          }`}
                        >
                          <FaBolt size={9} /> {postTriggerCount}
                        </span>
                      )}
                    </div>
                  </div>
                  <div
                    className={C.canvasNodeUrl}
                    title={getApiUrl(step.node.api_config_id)}
                  >
                    <span className={C.canvasNodeMethod}>
                      {getApiMethod(step.node.api_config_id)}
                    </span>
                    {displayUrlOf(getApiUrl(step.node.api_config_id)) ||
                      "Unknown API"}
                  </div>
                </div>
              </React.Fragment>
            );
          })}
          <FaArrowRight className={C.canvasArrow} />
          <div className={C.canvasStartNode}>
            <span className={C.canvasNodeLabel}>End</span>
            <div className={C.canvasNodeSub}>Flow completed</div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={C.canvasContainer}>
      {chains.length === 0 ? (
        <div className={C.emptyState}>
          <p>No chains yet. Create your first chain.</p>
        </div>
      ) : (
        <div className={C.canvasGrid}>
          {chains.map((chain) => (
            <div key={chain.chain_id} className={C.canvasCard}>
              {editingChainId === chain.chain_id && editForm
                ? renderEditableChain()
                : renderViewOnlyChain(chain)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CanvasView;